import {Eye, Pen, Trash2} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import DeleteConfirm from "@/components/forms/DeleteConfirm.tsx";
import AuditMetadata from "@/components/management/AuditMetadata.tsx";
import CharacterDetails from "@/characters/admin/details/CharacterDetails.tsx";
import {Character} from "@/characters/model/Character.ts";
import {useDeleteCharacterMutation} from "@/characters/CharacterQueries.ts";
import {isAxiosError} from "axios";

export default function CharacterDetail({character, onDisplay, onEdit, onDeleted}: {
    character: Character;
    onDisplay: () => void;
    onEdit: () => void;
    onDeleted: () => void;
}) {
    const deleteMutation = useDeleteCharacterMutation();
    const remove = async () => {
        try {
            await deleteMutation.mutateAsync(character.id);
            onDeleted();
        } catch {
            // The mutation error is rendered below.
        }
    };
    const deleteError = deleteMutation.error
        ? isAxiosError(deleteMutation.error) && deleteMutation.error.response?.status === 409
            ? "Der Charakter wird noch in einem Encounter verwendet. Entferne zuerst die zugehörigen Combatants."
            : "Der Charakter konnte nicht gelöscht werden."
        : undefined;

    return (
        <article className="flex size-full min-h-0 flex-col gap-3">
            <title>{`${character.name} | DireDawn`}</title>
            <header className="flex shrink-0 flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onDisplay}><Eye/> Anzeigen</Button>
                    <Button variant="outline" size="sm" onClick={onEdit}><Pen/> Bearbeiten</Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <AuditMetadata createdAt={character.createdAt} updatedAt={character.updatedAt}/>
                    <DeleteConfirm title={`${character.name} global löschen?`}
                                   description="Der Charakter wird aus dem globalen Katalog entfernt. Verwendete Charaktere können nicht gelöscht werden."
                                   onConfirm={remove}>
                        <Button size="icon-sm" variant="destructive" title="Löschen"
                                disabled={deleteMutation.isPending}
                                onSelect={event => event.preventDefault()}><Trash2/></Button>
                    </DeleteConfirm>
                </div>
            </header>
            {deleteError && <p role="alert"
                               className="shrink-0 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {deleteError}
            </p>}
            <div className="min-h-0 flex-1 overflow-auto"><CharacterDetails character={character}/></div>
        </article>
    );
}
